import React, {
  ChangeEvent,
  Dispatch,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import Modal from "../Modal";
import { useFormik } from "formik";
import Input from "../../../generic/dataEntry/Input/Input";
import { useAppDispatch, useAppSelector } from "../../../../hooks/redux";
import { IRoute } from "../../../../models/order";
import { orderSlice } from "../../../../store/slices/orderSlice";
import {
  createOrder,
  fetchOrders,
  getOrder,
  setCreatingFromCallFlag,
  setCurrentPage,
  updateOrder,
} from "../../../../store/actions/orderActions";
import CustomDatePicker from "../../../generic/dataEntry/CustomDatePicker/CustomDatePicker";
import Checkbox from "../../../generic/dataEntry/Checkbox";
import Button from "../../../generic/buttons/Button";
import address_drop_blue from "../../../../assets/icons/address_drop_blue.svg";
import address_drop_green from "../../../../assets/icons/address_drop_green.svg";
import CustomTimePicker from "../../../generic/dataEntry/CustomTimePicker";
import CustomSelect from "../../../generic/dataEntry/CustomSelect/CustomSelect";
import { CustomSelectOption } from "../../../../models";
import Textarea from "../../../generic/dataEntry/Textarea";
import { fetchBrigades } from "../../../../store/actions/brigadeActions";
import {
  addMedicalInstitution,
  fetchMedicalInstitutions,
} from "../../../../store/actions/medicalActions";
import classes from "../Modal/Modal.module.scss";
import IconButton from "../../../generic/buttons/IconButton";
import {
  fetchClients,
  fetchOrderHistory,
} from "../../../../store/actions/clientActions";
import { continueWaitingCall } from "../../../../store/actions/callActions";
import AddressInputWithSuggestions from "../../AddressInputWithSuggestions/AddressInputWithSuggestions";

interface IModalEditOrderProps {
  close: any;
  editID?: number;
  editSuccess: Dispatch<any>;
  fromCall?: boolean;
  makeCopy?: boolean;
}

function ModalEditOrder({
  close,
  editID,
  editSuccess,
  makeCopy,
  fromCall,
}: IModalEditOrderProps) {
  const dispatch = useAppDispatch();
  const {
    pageSize,
    isCreating,
    isUpdating,
    paymentTypes,
    statuses,
    order,
    fieldError,
    routeError,
    currentPage,
    filters,
    creatingFromCall,
  } = useAppSelector((state) => state.order);
  const brigades = useAppSelector((state) => state.brigade.list);
  const medicalInstitutions = useAppSelector((state) => state.medical.list);
  const clients = useAppSelector((state) => state.client.list);
  const { client_phone, client_uuid } = useAppSelector(
    (state) => state.call.data
  );
  const modalTitle = makeCopy
    ? `Копирование заявки`
    : editID
    ? `Редактировать заявку`
    : `Создать заявку`;
  const [routes, setRoutes] = useState<IRoute[]>([]);

  useEffect(() => {
    if (editID) {
      dispatch(getOrder({ id: editID }));
    }
  }, [dispatch, editID]);

  useEffect(() => {
    dispatch(fetchClients({ pageSize: 0 }));
  }, [dispatch]);

  const existingClient =
    creatingFromCall &&
    client_uuid &&
    clients.find((client) => client.client_uuid === client_uuid);

  const initialFormValues = editID
    ? {
        client_uuid: makeCopy ? order.client_uuid : "",
        fio: order.client_fio || "",
        phone: order.client_phone || "",
        weight: order.client_weight || "",
        routes: [],
      }
    : {
        client_uuid: existingClient ? existingClient.client_uuid : "",
        fio: existingClient ? existingClient.fio : "",
        phone: creatingFromCall ? client_phone : "",
        weight: existingClient ? existingClient.weight : "",
        routes: [],
      };

  const { values, touched, handleChange, handleReset } = useFormik({
    initialValues: initialFormValues,
    enableReinitialize: true,
    onSubmit: () => {},
  });

  useEffect(() => {
    dispatch(fetchBrigades({ page: 1, pageSize: 0 }));
    dispatch(fetchMedicalInstitutions({ page: 1, pageSize: 0 }));
  }, [dispatch]);

  const clientOptions = useMemo(() => {
    const list = clients.map((client) => ({
      value: client.client_uuid,
      label: client.fio,
    }));
    list.unshift({ label: "Не выбран", value: "" });
    return list;
  }, [clients]);

  const paymentTypeOptions = useMemo(() => {
    return paymentTypes.map((type) => ({
      value: type.id,
      label: type.name,
    }));
  }, [paymentTypes]);

  const medicalInstitutionOptions = useMemo(() => {
    const list = medicalInstitutions.map((item) => ({
      label: item.medical_name,
      value: item.medical_uuid,
    }));

    list.unshift({ label: "Не выбран", value: "" });
    return list;
  }, [medicalInstitutions]);

  const statusOptions = useMemo(() => {
    return statuses.map((status) => ({
      value: status.id,
      label: status.name,
    }));
  }, [statuses]);

  const brigadeOptions = useMemo(() => {
    const list = brigades.map((item) => ({
      label: item.brigade,
      value: String(item.id),
    }));

    list.unshift({ label: "Не выбрана", value: "" });
    return list;
  }, [brigades]);

  const handleSelectChange = (e: CustomSelectOption, name: string) => {
    handleChange({ target: { name, value: e.value } });

    if (name === "client_uuid") {
      const selectedClient = clients.find(
        (client) => client.client_uuid === e.value
      );

      if (selectedClient) {
        handleChange({ target: { name: "fio", value: selectedClient.fio } });
        handleChange({
          target: { name: "phone", value: selectedClient.phone },
        });
        handleChange({
          target: { name: "birthday", value: selectedClient.birthday },
        });
        handleChange({
          target: { name: "weight", value: selectedClient.weight },
        });
        handleChange({
          target: { name: "height", value: selectedClient.height },
        });
        dispatch(orderSlice.actions.clearFieldError("fio"));
        dispatch(orderSlice.actions.clearFieldError("phone"));
        dispatch(orderSlice.actions.clearFieldError("birthday"));
        dispatch(orderSlice.actions.clearFieldError("weight"));
        dispatch(orderSlice.actions.clearFieldError("height"));
      } else {
        handleChange({ target: { name: "fio", value: "" } });
        handleChange({ target: { name: "phone", value: "" } });
        handleChange({ target: { name: "birthday", value: "" } });
        handleChange({ target: { name: "weight", value: "" } });
        handleChange({ target: { name: "height", value: "" } });
      }
    }
  };

  const inputChangeHandler = useCallback(
    (e: React.ChangeEvent) => {
      handleChange(e);
      const fieldName: any = e.target.getAttribute("name");

      if (fieldError[fieldName]) {
        dispatch(orderSlice.actions.clearFieldError(fieldName));
      }
    },
    [handleChange, dispatch, fieldError]
  );

  const dateChangeHandler = (date: string, name: string) => {
    handleChange({
      target: { name, value: date },
    });

    if (fieldError[name]) {
      dispatch(orderSlice.actions.clearFieldError(name));
    }
  };

  const addNewAddress = async (addressData: {
    medical_name: string;
    city: string;
    address: string;
    entrance: string;
    floor: string;
    flat: string;
    lift: boolean;
  }) => {
    try {
      const medicalId = await dispatch(addMedicalInstitution(addressData));
      if (!medicalId) return null;
      const updatedList = await dispatch(
        fetchMedicalInstitutions({ page: 1, pageSize: 0 })
      );
      let newMedical = updatedList?.find((item) => item.id === medicalId);
      if (!newMedical) {
        await new Promise((resolve) => setTimeout(resolve, 100));
        await dispatch(fetchMedicalInstitutions({ page: 1, pageSize: 0 }));
        newMedical = medicalInstitutions.find((item) => item.id === medicalId);
      }

      if (!newMedical) {
        console.error("Добавленный адрес не найден");
        return null;
      }

      return {
        id: medicalId,
        medical_uuid: newMedical.medical_uuid,
        ...addressData,
      };
    } catch (error) {
      console.error("Ошибка при добавлении адреса:", error);
      return null;
    }
  };

  const processRoutesBeforeSubmit = async () => {
    const updatedRoutes = [...routes];

    for (let i = 0; i < updatedRoutes.length; i++) {
      const route = updatedRoutes[i];

      if (!route.medical_uuid_from && route.city_from && route.address_from) {
        const result = await addNewAddress({
          medical_name: `${route.address_from}, ${route.city_from}`,
          city: route.city_from,
          address: route.address_from,
          entrance: route.entrance_from || "",
          floor: route.floor_from || "",
          flat: route.flat_from || "",
          lift: route.lift_from,
        });

        if (result) {
          updatedRoutes[i] = {
            ...route,
            medical_uuid_from: result.medical_uuid,
            city_from: result.city,
            address_from: result.address,
            entrance_from: result.entrance,
            floor_from: String(result.floor),
            flat_from: result.flat,
            lift_from: result.lift,
          };
        }
        console.log("updatedRoutes", updatedRoutes);
      }

      if (!route.medical_uuid_to && route.city_to && route.address_to) {
        const result = await addNewAddress({
          medical_name: `${route.address_to}, ${route.city_to}`,
          city: route.city_to,
          address: route.address_to,
          entrance: route.entrance_to || "",
          floor: route.floor_to || "",
          flat: route.flat_to || "",
          lift: route.lift_to,
        });

        if (result) {
          updatedRoutes[i] = {
            ...route,
            medical_uuid_to: result.medical_uuid,
            city_to: result.city,
            address_to: result.address,
            entrance_to: result.entrance,
            floor_to: String(result.floor),
            flat_to: result.flat,
            lift_to: result.lift,
          };
        }
      }
    }

    return updatedRoutes;
  };
  const saveHandler = async () => {
    try {
      const processedRoutes = await processRoutesBeforeSubmit();
      setRoutes(processedRoutes);

      const isOrderCreated = await dispatch(
        createOrder({
          ...values,
          routes: processedRoutes,
        })
      );

      if (isOrderCreated) {
        dispatch(setCurrentPage(1));
        await dispatch(fetchOrders({ page: 1, pageSize, ...filters }));
        close();
        editSuccess(true);
        handleReset(initialFormValues);

        if (creatingFromCall) {
          dispatch(setCreatingFromCallFlag(false));
          dispatch(continueWaitingCall());
        }
      }
    } catch (error) {
      console.error("Ошибка при сохранении заявки:", error);
    }
  };

  const copyHandler = async () => {
    try {
      const processedRoutes = await processRoutesBeforeSubmit();
      setRoutes(processedRoutes);

      const isOrderCreated = await dispatch(
        createOrder({
          ...values,
          routes: processedRoutes,
        })
      );

      if (isOrderCreated) {
        await dispatch(fetchOrders({ page: 1, pageSize, ...filters }));
        await dispatch(fetchOrderHistory(order.client_uuid));
        close();
        editSuccess(true);
        handleReset(initialFormValues);
      }
    } catch (error) {
      console.error("Ошибка при копировании заявки:", error);
    }
  };

  const updateHandler = async () => {
    try {
      const processedRoutes = await processRoutesBeforeSubmit();
      setRoutes(processedRoutes);

      const isUpdated = await dispatch(
        updateOrder({
          ...values,
          routes: processedRoutes,
        })
      );

      if (isUpdated) {
        editSuccess(true);
        await dispatch(
          fetchOrders({ page: currentPage, pageSize, ...filters })
        );
        await dispatch(fetchOrderHistory(order.client_uuid));
        await dispatch(getOrder({ id: order.id }));
        close();
        handleReset(initialFormValues);
      }
    } catch (error) {
      console.error("Ошибка при обновлении заявки:", error);
    }
  };

  const closeHandler = () => {
    close();
    handleReset(initialFormValues);
    dispatch(orderSlice.actions.clearAllFieldErrors());
    dispatch(orderSlice.actions.clearAllRouteErrors());
    if (creatingFromCall) {
      dispatch(setCreatingFromCallFlag(false));
      dispatch(continueWaitingCall());
    }
  };

  useEffect(() => {
    if (editID) {
      const routes = [
        {
          request_id: order.id,
          address_from: order.address_from,
          address_to: order.address_to,
          brigade_id: order.brigade_id,
          city_from: order.city_from,
          city_to: order.city_to,
          entrance_from: order.entrance_from,
          entrance_to: order.entrance_to,
          flat_from: order.flat_from,
          flat_to: order.flat_to,
          floor_from: order.floor_from ? String(order.floor_from) : "",
          floor_to: order.floor_to ? String(order.floor_to) : "",
          lift_from: order.lift_from,
          lift_to: order.lift_to,
          medical_uuid_from: order.medical_uuid_from,
          medical_uuid_to: order.medical_uuid_to,
          payment_type_id: order.payment_type_id || "1",
          price: order.price ? String(order.price) : "",
          request_comment: order.comment,
          request_date: order.request_date,
          request_time: order.request_time,
          status_id: makeCopy ? 1 : order.status_id,
        },
      ];

      setRoutes(routes);
    }
  }, [order, editID, makeCopy]);

  const addRoute = () => {
    const newRoutes = [
      ...routes,
      {
        address_from: "",
        address_to: "",
        brigade_id: 0,
        city_from: "",
        city_to: "",
        entrance_from: "",
        entrance_to: "",
        flat_from: "",
        flat_to: "",
        floor_from: "",
        floor_to: "",
        lift_from: false,
        lift_to: false,
        medical_uuid_from: "",
        medical_uuid_to: "",
        payment_type_id: "1",
        price: "",
        request_comment: "",
        request_date: "",
        request_time: "",
        status_id: 0,
      },
    ];

    dispatch(orderSlice.actions.clearFieldError("routes"));
    setRoutes(newRoutes);
  };

  const deleteRoute = useCallback(
    (id: number) => {
      const newRoutes = routes.filter((elem, index) => index !== id);
      setRoutes(newRoutes);
    },
    [routes]
  );

  useEffect(() => {
    handleChange({ target: { name: "routes", value: routes } });
  }, [handleChange, routes]);

  const handleChangeRouteInput = useCallback(
    (id: number, element: ChangeEvent<HTMLInputElement>) => {
      const elTarget = element.target;
      let value: boolean | string;

      if (elTarget.type === "checkbox") {
        value = elTarget.checked;
      } else {
        value = elTarget.value;
      }

      dispatch(
        orderSlice.actions.clearRouteFieldError({
          routeID: id,
          fieldName: elTarget.name,
        })
      );

      let newRoutes = [...routes];
      let newRoute = { ...routes[id], [`${elTarget.name}`]: value };
      newRoutes.splice(id, 1, newRoute);
      setRoutes(newRoutes);
    },
    [routes, dispatch]
  );

  const handleChangeRouteTextarea = useCallback(
    (id: number, element: ChangeEvent<HTMLTextAreaElement>) => {
      const elTarget = element.target;

      dispatch(
        orderSlice.actions.clearRouteFieldError({
          routeID: id,
          fieldName: elTarget.name,
        })
      );

      let newRoutes = [...routes];
      let newRoute = { ...routes[id], [`${elTarget.name}`]: elTarget.value };
      newRoutes.splice(id, 1, newRoute);
      setRoutes(newRoutes);
    },
    [routes, dispatch]
  );

  const handleChangeRouteSelect = useCallback(
    (id: number, value: string, name: string) => {
      let newRoutes = [...routes];
      let newRoute;

      if (name === "medical_uuid_from") {
        const medicalFrom = medicalInstitutions.find(
          (item) => item.medical_uuid === value
        );

        newRoute = {
          ...routes[id],
          [`${name}`]: value,
          city_from: medicalFrom ? medicalFrom.city : "",
          address_from: medicalFrom ? medicalFrom.address : "",
          entrance_from: medicalFrom ? medicalFrom.entrance : "",
          floor_from: medicalFrom ? String(medicalFrom.floor) : "",
          flat_from: medicalFrom ? medicalFrom.flat : "",
          lift_from: medicalFrom ? Boolean(medicalFrom.flat) : false,
        };

        medicalFrom?.city &&
          dispatch(
            orderSlice.actions.clearRouteFieldError({
              routeID: id,
              fieldName: "city_from",
            })
          );
        medicalFrom?.address &&
          dispatch(
            orderSlice.actions.clearRouteFieldError({
              routeID: id,
              fieldName: "address_from",
            })
          );
      } else if (name === "medical_uuid_to") {
        const medicalTo = medicalInstitutions.find(
          (item) => item.medical_uuid === value
        );

        newRoute = {
          ...routes[id],
          [`${name}`]: value,
          city_to: medicalTo ? medicalTo.city : "",
          address_to: medicalTo ? medicalTo.address : "",
          entrance_to: medicalTo ? medicalTo.entrance : "",
          floor_to: medicalTo ? String(medicalTo.floor) : "",
          flat_to: medicalTo ? medicalTo.flat : "",
          lift_to: medicalTo ? Boolean(medicalTo.flat) : false,
        };

        medicalTo?.city &&
          dispatch(
            orderSlice.actions.clearRouteFieldError({
              routeID: id,
              fieldName: "city_to",
            })
          );
        medicalTo?.address &&
          dispatch(
            orderSlice.actions.clearRouteFieldError({
              routeID: id,
              fieldName: "address_to",
            })
          );
      } else {
        newRoute = { ...routes[id], [`${name}`]: value };
      }

      newRoutes.splice(id, 1, newRoute);
      setRoutes(newRoutes);
    },
    [routes, medicalInstitutions, dispatch]
  );

  const handleChangeRouteDate = useCallback(
    (id: number, value: string, name: string) => {
      let newRoutes = [...routes];
      let newRoute = { ...routes[id], [`${name}`]: value };
      value &&
        dispatch(
          orderSlice.actions.clearRouteFieldError({
            routeID: id,
            fieldName: name,
          })
        );
      newRoutes.splice(id, 1, newRoute);
      setRoutes(newRoutes);
    },
    [routes, dispatch]
  );

  const swapAddresses = useCallback(
    (id: number, route: IRoute) => {
      let newRoutes = [...routes];
      let newRoute = {
        ...routes[id],
        medical_uuid_from: route.medical_uuid_to,
        medical_uuid_to: route.medical_uuid_from,
        city_from: route.city_to,
        city_to: route.city_from,
        address_from: route.address_to,
        address_to: route.address_from,
        entrance_from: route.entrance_to,
        entrance_to: route.entrance_from,
        flat_from: route.flat_to,
        flat_to: route.flat_from,
        floor_from: route.floor_to,
        floor_to: route.floor_from,
        lift_from: route.lift_to,
        lift_to: route.lift_from,
      };
      newRoutes.splice(id, 1, newRoute);
      setRoutes(newRoutes);
    },
    [routes]
  );

  const routesTemplate = useMemo(() => {
    return routes.map((route: IRoute, id) => {
      const medicalFrom = medicalInstitutions.find(
        (item) => item.medical_uuid === route.medical_uuid_from
      );
      const medicalTo = medicalInstitutions.find(
        (item) => item.medical_uuid === route.medical_uuid_to
      );

      return (
        <div className={classes.form_section} key={id}>
          {!editID && (
            <IconButton
              className={classes.button_delete_route}
              onClick={() => deleteRoute(id)}
              icon={"trash"}
            />
          )}
          {!editID && (
            <div className={classes.form_section_title}>
              <span>Маршрут {id + 1}</span>
            </div>
          )}
          <div className={classes.form_group}>
            <div
              className={[
                classes.form_group_title,
                classes.form_group_title_city,
                classes.form_group_title_city_from,
              ].join(" ")}
            >
              <img src={address_drop_blue} alt="" />
              <span>Откуда</span>
            </div>
            <div className={classes.form_field}>
              <CustomSelect
                name={"medical_uuid_from"}
                label={"Начальный адрес"}
                value={medicalInstitutionOptions.find(
                  (elem) => elem.value === String(route.medical_uuid_from)
                )}
                options={medicalInstitutionOptions}
                onChange={(e: CustomSelectOption) => {
                  return handleChangeRouteSelect(
                    id,
                    e.value,
                    "medical_uuid_from"
                  );
                }}
                isSearchable
                placeholder={"Не выбран"}
              />
            </div>
            <div
              className={[
                classes.form_group_container,
                classes.form_group_container_address,
              ].join(" ")}
            >
              <div className={classes.form_field}>
                <Input
                  name={`city_from`}
                  label={"Город"}
                  value={medicalFrom ? medicalFrom.city : route.city_from}
                  onChange={(element: React.ChangeEvent<HTMLInputElement>) => {
                    return handleChangeRouteInput(id, element);
                  }}
                  valid={routeError && !routeError[id]?.city_from}
                  errorMessage={routeError && routeError[id]?.city_from}
                  required
                  shouldValidate
                  disabled={Boolean(route.medical_uuid_from)}
                />
              </div>
              <div className={classes.form_field}>
                <AddressInputWithSuggestions
                  value={route.address_from ?? medicalFrom?.address}
                  onChange={(val) => {
                    const fakeEvent = {
                      target: { name: "address_from", value: val },
                    } as unknown as React.ChangeEvent<HTMLInputElement>;
                    handleChangeRouteInput(id, fakeEvent);
                  }}
                  disabled={Boolean(route.medical_uuid_from)}
                  onAddressSelect={(addr) => {
                    const newRoutes = [...routes];
                    newRoutes[id] = {
                      ...newRoutes[id],
                      address_from: addr.fullAddress,
                      city_from: newRoutes[id].city_from,
                    };
                    setRoutes(newRoutes);
                    dispatch(
                      orderSlice.actions.clearRouteFieldError({
                        routeID: id,
                        fieldName: "address_from",
                      })
                    );
                  }}
                  label="Адрес (улица, дом)"
                />
              </div>
              <div className={classes.form_field}>
                <Input
                  name={"entrance_from"}
                  label={"Подъезд"}
                  value={
                    medicalFrom ? medicalFrom.entrance : route.entrance_from
                  }
                  onChange={(element: React.ChangeEvent<HTMLInputElement>) => {
                    return handleChangeRouteInput(id, element);
                  }}
                  valid={routeError && !routeError[id]?.entrance_from}
                  errorMessage={routeError && routeError[id]?.entrance_from}
                  disabled={Boolean(route.medical_uuid_from)}
                />
              </div>
              <div className={classes.form_field}>
                <Input
                  name={"floor_from"}
                  label={"Этаж"}
                  value={
                    medicalFrom
                      ? typeof medicalFrom.floor === "object"
                        ? ""
                        : String(medicalFrom.floor)
                      : route.floor_from
                  }
                  onChange={(element: React.ChangeEvent<HTMLInputElement>) => {
                    return handleChangeRouteInput(id, element);
                  }}
                  valid={routeError && !routeError[id]?.floor_from}
                  errorMessage={routeError && routeError[id]?.floor_from}
                  disabled={Boolean(route.medical_uuid_from)}
                />
              </div>
              <div className={classes.form_field}>
                <Input
                  name={"flat_from"}
                  label={"Квартира"}
                  value={medicalFrom ? medicalFrom.flat : route.flat_from}
                  onChange={(element: React.ChangeEvent<HTMLInputElement>) => {
                    return handleChangeRouteInput(id, element);
                  }}
                  valid={routeError && !routeError[id]?.flat_from}
                  errorMessage={routeError && routeError[id]?.flat_from}
                  disabled={Boolean(route.medical_uuid_from)}
                />
              </div>
              <div className={classes.form_field}>
                <Checkbox
                  name={"lift_from"}
                  text={"Есть лифт"}
                  checked={medicalFrom ? medicalFrom.lift : route.lift_from}
                  onChange={(element: React.ChangeEvent<HTMLInputElement>) => {
                    return handleChangeRouteInput(id, element);
                  }}
                  disabled={Boolean(route.medical_uuid_from)}
                />
              </div>
            </div>
          </div>
          <div className={classes.form_group}>
            <Button
              icon={`refresh`}
              className={classes.button_swap_addresses}
              onClick={() => swapAddresses(id, route)}
              color={`white`}
            >
              Поменять адреса
            </Button>
          </div>
          <div className={classes.form_group}>
            <div
              className={[
                classes.form_group_title,
                classes.form_group_title_city,
                classes.form_group_title_city_to,
              ].join(" ")}
            >
              <img src={address_drop_green} alt="" />
              <span>Куда</span>
            </div>
            <div className={classes.form_field}>
              <CustomSelect
                name={"medical_uuid_to"}
                label={"Конечный адрес"}
                value={medicalInstitutionOptions.find(
                  (elem) => elem.value === String(route.medical_uuid_to)
                )}
                options={medicalInstitutionOptions}
                onChange={(e: CustomSelectOption) => {
                  return handleChangeRouteSelect(
                    id,
                    e.value,
                    "medical_uuid_to"
                  );
                }}
                isSearchable
                placeholder={"Не выбран"}
              />
            </div>
            <div
              className={[
                classes.form_group_container,
                classes.form_group_container_address,
              ].join(" ")}
            >
              <div className={classes.form_field}>
                <Input
                  name={"city_to"}
                  label={"Город"}
                  value={medicalTo ? medicalTo.city : route.city_to}
                  onChange={(element: React.ChangeEvent<HTMLInputElement>) => {
                    return handleChangeRouteInput(id, element);
                  }}
                  valid={routeError && !routeError[id]?.city_to}
                  errorMessage={routeError && routeError[id]?.city_to}
                  shouldValidate
                  required
                  disabled={Boolean(route.medical_uuid_to)}
                />
              </div>
              <div className={classes.form_field}>
                <AddressInputWithSuggestions
                  value={route.address_to ?? medicalFrom?.address}
                  onChange={(val) => {
                    const fakeEvent = {
                      target: { name: "address_to", value: val },
                    } as unknown as React.ChangeEvent<HTMLInputElement>;
                    handleChangeRouteInput(id, fakeEvent);
                  }}
                  disabled={Boolean(route.medical_uuid_to)}
                  onAddressSelect={(addr) => {
                    const newRoutes = [...routes];
                    newRoutes[id] = {
                      ...newRoutes[id],
                      address_to: addr.fullAddress,
                      city_to: newRoutes[id].city_to,
                    };
                    setRoutes(newRoutes);
                    dispatch(
                      orderSlice.actions.clearRouteFieldError({
                        routeID: id,
                        fieldName: "address_to",
                      })
                    );
                  }}
                  label="Адрес (улица, дом)"
                />
              </div>
              <div className={classes.form_field}>
                <Input
                  name={"entrance_to"}
                  label={"Подъезд"}
                  value={medicalTo ? medicalTo.entrance : route.entrance_to}
                  onChange={(element: React.ChangeEvent<HTMLInputElement>) => {
                    return handleChangeRouteInput(id, element);
                  }}
                  valid={routeError && !routeError[id]?.entrance_to}
                  errorMessage={routeError && routeError[id]?.entrance_to}
                  disabled={Boolean(route.medical_uuid_to)}
                />
              </div>
              <div className={classes.form_field}>
                <Input
                  name={"floor_to"}
                  label={"Этаж"}
                  value={
                    medicalTo
                      ? typeof medicalTo.floor === "object"
                        ? ""
                        : String(medicalTo.floor)
                      : route.floor_to
                  }
                  onChange={(element: React.ChangeEvent<HTMLInputElement>) => {
                    return handleChangeRouteInput(id, element);
                  }}
                  valid={routeError && !routeError[id]?.floor_to}
                  errorMessage={routeError && routeError[id]?.floor_to}
                  disabled={Boolean(route.medical_uuid_to)}
                />
              </div>
              <div className={classes.form_field}>
                <Input
                  name={"flat_to"}
                  label={"Квартира"}
                  value={medicalTo ? medicalTo.flat : route.flat_to}
                  onChange={(element: React.ChangeEvent<HTMLInputElement>) => {
                    return handleChangeRouteInput(id, element);
                  }}
                  valid={routeError && !routeError[id]?.flat_to}
                  errorMessage={routeError && routeError[id]?.flat_to}
                  disabled={Boolean(route.medical_uuid_to)}
                />
              </div>
              <div className={classes.form_field}>
                <Checkbox
                  name={"lift_to"}
                  text={"Есть лифт"}
                  checked={medicalTo ? medicalTo.lift : route.lift_to}
                  onChange={(element: React.ChangeEvent<HTMLInputElement>) => {
                    return handleChangeRouteInput(id, element);
                  }}
                  disabled={Boolean(route.medical_uuid_to)}
                />
              </div>
            </div>
          </div>
          <hr className={classes.hr} />
          <div className={classes.form_group}>
            <div className={classes.form_group_title}>Подача и оплата</div>
            <div
              className={[
                classes.form_group_container,
                classes.form_group_container_route_options,
              ].join(" ")}
            >
              <div
                className={[classes.form_field, classes.form_field_date].join(
                  " "
                )}
              >
                <CustomDatePicker
                  name={"request_date"}
                  label={"Дата подачи"}
                  curDate={route.request_date}
                  onChangeDate={(date: string) => {
                    return handleChangeRouteDate(id, date, "request_date");
                  }}
                  valid={routeError && !routeError[id]?.request_date}
                  errorMessage={routeError && routeError[id]?.request_date}
                  required
                  shouldValidate
                />
              </div>
              <div
                className={[classes.form_field, classes.form_field_time].join(
                  " "
                )}
              >
                <CustomTimePicker
                  name={"request_time"}
                  label={"Время подачи"}
                  curTime={route.request_time}
                  onChangeTime={(time: string) => {
                    return handleChangeRouteDate(id, time, "request_time");
                  }}
                  valid={routeError && !routeError[id]?.request_time}
                  errorMessage={routeError && routeError[id]?.request_time}
                  shouldValidate
                />
              </div>
              <div
                className={[
                  classes.form_field,
                  classes.form_field_brigade,
                ].join(" ")}
              >
                <CustomSelect
                  name={"brigade_id"}
                  label={"Бригада"}
                  value={brigadeOptions.find(
                    (elem) => elem.value === String(route.brigade_id)
                  )}
                  options={brigadeOptions}
                  onChange={(e: CustomSelectOption) => {
                    return handleChangeRouteSelect(id, e.value, "brigade_id");
                  }}
                  isSearchable
                  placeholder={"Не выбрана"}
                />
              </div>
              <div
                className={[classes.form_field, classes.form_field_price].join(
                  " "
                )}
              >
                <Input
                  name={"price"}
                  label={"Стоимость (руб)"}
                  value={route.price}
                  onChange={(element: React.ChangeEvent<HTMLInputElement>) => {
                    return handleChangeRouteInput(id, element);
                  }}
                />
              </div>
              <div
                className={[
                  classes.form_field,
                  classes.form_field_payment_type,
                ].join(" ")}
              >
                <CustomSelect
                  name={"payment_type_id"}
                  label={"Способ оплаты"}
                  value={paymentTypeOptions.find(
                    (elem) => elem.value == String(route.payment_type_id ?? "1")
                  )}
                  options={paymentTypeOptions}
                  onChange={(e: CustomSelectOption) => {
                    return handleChangeRouteSelect(
                      id,
                      e.value,
                      "payment_type_id"
                    );
                  }}
                  isSearchable
                  placeholder={"Не выбран"}
                />
              </div>
              <div
                className={[
                  classes.form_field,
                  classes.form_field_comment,
                ].join(" ")}
              >
                <Textarea
                  name={"request_comment"}
                  label={"Комментарий"}
                  value={route.request_comment}
                  onChange={(
                    element: React.ChangeEvent<HTMLTextAreaElement>
                  ) => {
                    return handleChangeRouteTextarea(id, element);
                  }}
                  valid={routeError && !routeError[id]?.request_comment}
                  errorMessage={routeError && routeError[id]?.request_comment}
                  shouldValidate
                  rows={4}
                />
              </div>
              <div
                className={[classes.form_field, classes.form_field_status].join(
                  " "
                )}
              >
                <CustomSelect
                  name={"status_id"}
                  label={"Статус"}
                  value={statusOptions.find(
                    (elem) => elem.value === Number(route.status_id)
                  )}
                  options={statusOptions}
                  onChange={(e: CustomSelectOption) => {
                    return handleChangeRouteSelect(id, e.value, "status_id");
                  }}
                  // valid={!error.role_id}
                  // shouldValidate
                  // errorMessage={error.role_id}
                  isSearchable
                  // required
                  placeholder={"Не выбран"}
                  maxMenuHeight={110}
                />
              </div>
            </div>
          </div>
        </div>
      );
    });
  }, [
    routes,
    handleChangeRouteInput,
    handleChangeRouteDate,
    handleChangeRouteSelect,
    paymentTypeOptions,
    handleChangeRouteTextarea,
    statusOptions,
    brigadeOptions,
    medicalInstitutions,
    medicalInstitutionOptions,
    deleteRoute,
    editID,
    routeError,
    swapAddresses,
  ]);

  return (
    <Modal
      title={modalTitle}
      close={closeHandler}
      dialogSizeSuffix={"w620"}
      submitClick={
        makeCopy ? copyHandler : editID ? updateHandler : saveHandler
      }
      cancelClick={closeHandler}
      submitDisabled={isUpdating || isCreating}
      submitLoading={isUpdating || isCreating}
    >
      <form className={classes.form}>
        <div className={classes.form_section}>
          <div className={classes.form_section_title}>Информация о клиенте</div>
          {!editID && (
            <div className={classes.form_field}>
              <CustomSelect
                onChange={(e: CustomSelectOption) => {
                  handleSelectChange(e, "client_uuid");
                }}
                label={"Клиент из базы"}
                value={clientOptions.find(
                  (elem) => elem.value === values.client_uuid
                )}
                options={clientOptions}
                name={"client_uuid"}
                isSearchable
                placeholder={"Не выбран"}
              />
            </div>
          )}
          <div className={classes.form_group}>
            {(!values.client_uuid || makeCopy) && (
              <div className={classes.form_field}>
                <Input
                  value={values.fio}
                  onChange={(e: React.ChangeEvent) => {
                    inputChangeHandler(e);
                  }}
                  name={"fio"}
                  label={"Полное имя (ФИО)"}
                  valid={!fieldError.fio}
                  touched={touched.fio}
                  errorMessage={fieldError.fio}
                  required
                  shouldValidate
                />
              </div>
            )}
            <div
              className={[
                classes.form_group_container,
                classes.form_group_container_client_data,
              ].join(" ")}
            >
              <div className={classes.form_field}>
                <Input
                  mask="+7 (999) 999-99-99"
                  value={values.phone}
                  onChange={(e: React.ChangeEvent) => {
                    inputChangeHandler(e);
                  }}
                  name={"phone"}
                  label={"Телефон"}
                  valid={!fieldError.phone}
                  touched={touched.phone}
                  required
                  shouldValidate
                  disabled={Boolean(values.client_uuid)}
                />
              </div>
              {/*Feat:Скрыли         
                <div className={classes.form_field}>
                <CustomDatePicker
                  curDate={values.birthday}
                  onChangeDate={(date: string) => {
                    dateChangeHandler(date, "birthday")
                  }}
                  name={"birthday"}
                  label={"Дата рождения"}
                  valid={!fieldError.birthday}
                  touched={touched.birthday}
                  required
                  shouldValidate
                  disabled={Boolean(values.client_uuid)}
                />
              </div>
              <div className={classes.form_field}>
                <Input
                  value={String(values.height)}
                  onChange={(e: React.ChangeEvent) => {
                    inputChangeHandler(e)
                  }}
                  name={"height"}
                  label={"Рост (см)"}
                  valid={!fieldError.height}
                  touched={touched.height}
                  required
                  shouldValidate
                  disabled={Boolean(values.client_uuid)}
                />
              </div>*/}
              <div className={classes.form_field}>
                <Input
                  value={String(values.weight)}
                  onChange={(e: React.ChangeEvent) => {
                    inputChangeHandler(e);
                  }}
                  name={"weight"}
                  label={"Вес (кг)"}
                  valid={!fieldError.weight}
                  touched={touched.weight}
                  required
                  shouldValidate
                  disabled={Boolean(values.client_uuid)}
                />
              </div>
            </div>
          </div>
        </div>
        {routesTemplate}
        {!editID && (
          <div className={classes.form_field}>
            <div className={classes.route_control}>
              {fieldError.routes && (
                <div className={classes.route_control_error}>
                  {fieldError.routes}
                </div>
              )}
              <Button
                color={"white"}
                className={classes.button_add_route}
                onClick={addRoute}
              >
                + Добавить маршрут
              </Button>
            </div>
          </div>
        )}
      </form>
    </Modal>
  );
}

export default ModalEditOrder;
